import {v4 as createUUID} from "uuid";
import {filterNullValues, pushToDataLayer, runSafe} from "../utils";
import {EventName} from "../enums/analytics/EventName";

type GetHistoryResponse = {
    count: number
}

export class Analytics {
    private static _instance: Analytics|null = null;

    public static readonly SESSION_UUID_KEY_NAME = "session_uuid";

    public static instance(): Analytics {
        if (Analytics._instance === null) {
            Analytics._instance = new Analytics();
        }

        return Analytics._instance;
    }

    public async getUserHistory(): Promise<GetHistoryResponse> {
        const response = await fetch(`/api/analytics/history?sessionId=${this.getSessionUuid()}`);

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message ?? "Failed get history");
        }

        return data;
    }

    public getSessionUuid() {
        let sessionUuid = localStorage.getItem(Analytics.SESSION_UUID_KEY_NAME);

        if (sessionUuid === null) {
            sessionUuid = createUUID();
            localStorage.setItem(Analytics.SESSION_UUID_KEY_NAME, sessionUuid);
        }

        return sessionUuid;
    }

    public sendEvent(
      name: string,
      category: string|null = null,
      label: string|null = null,
      params: Record<string, unknown> = {}
    ): void {
        pushToDataLayer(filterNullValues({
            ...params,
            "event": name,
            "category": category,
            "label": label,
        }));
    }

    public debugB24(): void {
        runSafe(() => {
            const debug = {
                hasTracker: !!window.b24Tracker,
                hasGuestApi: !!window.b24Tracker?.guest,
                hasGetTrace: !!window.b24Tracker?.guest?.getTrace,

                trace: window.b24Tracker?.guest?.getTrace?.() || null,

                localStorage: Object.fromEntries(
                    Object.keys(localStorage)
                        .filter(k => k.toLowerCase().includes("b24"))
                        .map(k => [k, localStorage.getItem(k)])
                ),

                cookiesEnabled: navigator.cookieEnabled,
                doNotTrack: navigator.doNotTrack,

                url: location.href,
                ts: Date.now(),
            };
            const blob = new Blob([JSON.stringify(debug)], { type: "application/json" });
            const apiPoint = "/api/analytics/debug-b24";

            navigator.sendBeacon?.(apiPoint, blob)
                || fetch(apiPoint, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(debug),
                    keepalive: true,
                });
        });
    }
}

export function getFirstImpressionIntersectObserver(category: string) {
    const handleIntersect: IntersectionObserverCallback = (entries) => {
        for (const entry of entries) {
            if (entry.intersectionRatio >= 0.5) {
                Analytics.instance().sendEvent(EventName.X_IMPRESSION, category);

                intersectObserver.disconnect();
                return;
            }
        }
    }
    const intersectObserver = new IntersectionObserver(handleIntersect, {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
    });

    return intersectObserver;
}